import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Cake, PartyPopper, Sparkles, X } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

export default function BirthdayGift({ customer, onGiftClaimed }) {
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [birthdayGift, setBirthdayGift] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [hasBirthdayToday, setHasBirthdayToday] = useState(false);

  useEffect(() => {
    checkBirthday();
  }, [customer]);

  const checkBirthday = () => {
    if (!customer?.birthDate) return;

    const today = new Date();
    const birthDate = new Date(customer.birthDate);
    
    // Vérifier si c'est l'anniversaire (même jour et mois)
    const isBirthday = 
      today.getDate() === birthDate.getDate() && 
      today.getMonth() === birthDate.getMonth();

    if (isBirthday) {
      setHasBirthdayToday(true);
      // Vérifier si le cadeau n'a pas déjà été réclamé cette année
      const lastClaimed = customer.lastBirthdayGiftYear;
      if (lastClaimed !== today.getFullYear()) {
        // Générer le cadeau
        const gifts = [
          { emoji: '🎁', name: 'Puff gratuite', description: 'Une Puff de votre choix offerte !' },
          { emoji: '🥤', name: 'Boisson offerte', description: 'Une boisson fraîche de votre choix !' },
          { emoji: '🍫', name: 'Friandise surprise', description: 'Une gourmandise offerte par la maison !' },
          { emoji: '💨', name: '-50% sur Puffs', description: '50% de réduction sur votre prochain achat de Puffs !' },
          { emoji: '🌿', name: '1g CBD offert', description: '1 gramme de CBD premium offert !' },
        ];
        const randomGift = gifts[Math.floor(Math.random() * gifts.length)];
        setBirthdayGift(randomGift);
        setShowBirthdayModal(true);
        
        // Lancer les confettis !
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }, 500);
      }
    }
  };

  const claimGift = async () => {
    setClaiming(true);
    try {
      await api.claimBirthdayGift(customer.id, birthdayGift);
      toast.success('🎂 Joyeux anniversaire ! Votre cadeau vous attend en magasin !', {
        duration: 5000
      });
      setShowBirthdayModal(false);
      if (onGiftClaimed) onGiftClaimed();
      
      // Plus de confettis !
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 }
      });
    } catch (error) {
      toast.error('🎁 Oups ! Impossible de récupérer votre cadeau. Réessayez ou contactez le magasin.');
    } finally {
      setClaiming(false);
    }
  };

  // Badge anniversaire si c'est son anniversaire
  if (hasBirthdayToday && !showBirthdayModal) {
    return (
      <motion.button
        onClick={() => setShowBirthdayModal(true)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-24 right-4 z-50"
      >
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ duration: 1, repeat: Infinity }}
          className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-yellow-500 flex items-center justify-center shadow-lg"
          style={{ boxShadow: '0 0 30px rgba(236, 72, 153, 0.6)' }}
        >
          <span className="text-3xl">🎂</span>
        </motion.div>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      {showBirthdayModal && birthdayGift && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setShowBirthdayModal(false)}
        >
          <motion.div
            initial={{ scale: 0.5, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0.5, rotate: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #EC4899 0%, #F59E0B 50%, #8B5CF6 100%)',
              boxShadow: '0 0 60px rgba(236, 72, 153, 0.5)'
            }}
          >
            {/* Fermer */}
            <button
              onClick={() => setShowBirthdayModal(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Contenu */}
            <div className="p-8 text-center">
              {/* Animation gâteau */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-7xl mb-4"
              >
                🎂
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-white mb-2"
              >
                Joyeux Anniversaire !
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-white/80 mb-6"
              >
                {customer?.name || 'Cher client'}, La Petite Épicerie vous souhaite une merveilleuse journée ! 🎉
              </motion.p>

              {/* Cadeau */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6"
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                  <span className="text-yellow-300 font-semibold">Votre cadeau</span>
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                </div>
                <div className="text-5xl mb-3">{birthdayGift.emoji}</div>
                <h3 className="text-xl font-bold text-white mb-1">{birthdayGift.name}</h3>
                <p className="text-white/80 text-sm">{birthdayGift.description}</p>
              </motion.div>

              {/* Bouton */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  onClick={claimGift}
                  disabled={claiming}
                  className="w-full py-4 text-lg font-bold rounded-xl bg-white text-pink-600 hover:bg-white/90"
                >
                  {claiming ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-pink-600 border-t-transparent rounded-full animate-spin" />
                      Récupération...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Gift className="w-5 h-5" />
                      Récupérer mon cadeau !
                    </span>
                  )}
                </Button>
              </motion.div>

              <p className="text-white/60 text-xs mt-4">
                Présentez ce cadeau en magasin aujourd'hui
              </p>
            </div>

            {/* Décorations */}
            <div className="absolute top-0 left-0 text-4xl opacity-30">🎈</div>
            <div className="absolute top-10 right-10 text-3xl opacity-30">🎉</div>
            <div className="absolute bottom-20 left-5 text-3xl opacity-30">✨</div>
            <div className="absolute bottom-10 right-10 text-4xl opacity-30">🎁</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Composant pour afficher le prochain anniversaire
export function BirthdayCountdown({ birthDate }) {
  if (!birthDate) return null;

  const today = new Date();
  const birth = new Date(birthDate);
  let nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  
  if (nextBirthday < today) {
    nextBirthday.setFullYear(today.getFullYear() + 1);
  }

  const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));

  if (daysUntil === 0) {
    return (
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-pink-500 to-yellow-500 text-white text-sm font-bold"
      >
        🎂 C'est votre anniversaire !
      </motion.div>
    );
  }

  if (daysUntil <= 7) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-500/20 text-pink-400 text-xs">
        🎂 Anniversaire dans {daysUntil} jour{daysUntil > 1 ? 's' : ''} !
      </div>
    );
  }

  return null;
}
